import { useState } from "react";
import { useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCompanies } from "@/hooks/use-companies";
import { azureApi, type AzureDeployment } from "@/api/azure";
import { Rocket, Server, ChevronDown, ChevronUp, RefreshCw, AlertTriangle } from "lucide-react";

const statusVariant: Record<string, "success" | "warning" | "danger" | "default" | "secondary"> = {
  running: "success",
  provisioning: "warning",
  pending: "secondary",
  stopped: "default",
  deallocated: "default",
  failed: "danger",
  deleted: "danger",
};

function stringify(val: unknown): string {
  if (val === null || val === undefined) return "";
  if (typeof val === "string") return val;
  return JSON.stringify(val, null, 2);
}

function DeploymentCard({ d }: { d: AzureDeployment & { companyName: string } }) {
  const [expanded, setExpanded] = useState(false);
  const queryClient = useQueryClient();
  const isFailed = d.status === "failed";

  const retry = useMutation({
    mutationFn: () => azureApi.startDeployment(d.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deployments", d.companyId] });
    },
  });

  // Safely extract error info from metadata — all values must be stringified
  const meta = d.metadata ?? {};
  const errorText = stringify(meta.error ?? meta.errorMessage ?? meta.failureReason ?? meta.rbacError) || null;
  const provisioningText = stringify(meta.provisioningOutput ?? meta.deploymentLog) || null;

  return (
    <Card>
      <CardContent className="py-4">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
              {isFailed ? (
                <AlertTriangle size={20} className="text-red-500" />
              ) : (
                <Server size={20} className="text-accent" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-text-primary">
                {String(d.resourceGroup || d.id.slice(0, 8))}
              </p>
              <p className="text-xs text-text-muted">
                {String(d.companyName)} · {String(d.region || "—")} · {String(d.vmSize || "—")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {d.ipAddress && <span className="text-xs text-text-muted font-mono">{String(d.ipAddress)}</span>}
            <Badge variant={statusVariant[d.status] ?? "default"}>
              {String(d.status)}
            </Badge>
            {isFailed && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); retry.mutate(); }}
                disabled={retry.isPending}
              >
                <RefreshCw size={14} className={retry.isPending ? "animate-spin mr-1" : "mr-1"} />
                Retry
              </Button>
            )}
            {expanded ? <ChevronUp size={16} className="text-text-muted" /> : <ChevronDown size={16} className="text-text-muted" />}
          </div>
        </div>

        {expanded && (
          <div className="mt-3 border-t border-border pt-3 space-y-2">
            {isFailed && errorText && (
              <div className="rounded-md bg-red-500/10 px-3 py-2">
                <p className="text-xs font-medium text-red-500 mb-1">Error</p>
                <p className="text-xs text-text-secondary font-mono whitespace-pre-wrap">
                  {errorText}
                </p>
              </div>
            )}
            {isFailed && !errorText && (
              <div className="rounded-md bg-red-500/10 px-3 py-2">
                <p className="text-xs text-text-secondary">
                  No error details available. The deployment may have failed during Azure provisioning.
                  Check the Azure Portal for more details, or retry the deployment.
                </p>
              </div>
            )}
            {provisioningText && (
              <div>
                <p className="text-xs font-medium text-text-muted mb-1">Provisioning Details</p>
                <p className="text-xs text-text-secondary font-mono whitespace-pre-wrap">
                  {provisioningText}
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-text-muted">Resource ID: </span>
                <span className="text-text-secondary font-mono">{String(d.resourceId || "—")}</span>
              </div>
              <div>
                <span className="text-text-muted">Provisioning State: </span>
                <span className="text-text-secondary">{String(d.provisioningState || "—")}</span>
              </div>
              <div>
                <span className="text-text-muted">Created: </span>
                <span className="text-text-secondary">{new Date(d.createdAt).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-text-muted">Updated: </span>
                <span className="text-text-secondary">{new Date(d.updatedAt).toLocaleString()}</span>
              </div>
            </div>
            {retry.error && (
              <p className="text-xs text-red-500">
                {"Retry failed: " + ((retry.error as Error).message ?? "Unknown error")}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function Deployments() {
  const { data: companies = [] } = useCompanies();

  // Use useQueries to avoid hooks-in-loop violation
  const deploymentResults = useQueries({
    queries: companies.map((c) => ({
      queryKey: ["deployments", c.id],
      queryFn: () => azureApi.listDeployments(c.id),
      enabled: !!c.id,
    })),
  });

  const allDeployments: Array<AzureDeployment & { companyName: string }> = [];
  for (let i = 0; i < companies.length; i++) {
    const result = deploymentResults[i];
    const company = companies[i];
    if (result?.data && company) {
      for (const d of result.data) {
        allDeployments.push({ ...d, companyName: company.name });
      }
    }
  }

  return (
    <CenterPanel title="Deployments">
      <p className="mb-4 text-sm text-text-secondary">
        {allDeployments.length} deployment{allDeployments.length !== 1 ? "s" : ""} across {companies.length} tenant{companies.length !== 1 ? "s" : ""}
      </p>

      {allDeployments.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Rocket size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">
              No deployments yet. Deploy agents from within a tenant workspace.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {allDeployments.map((d) => (
            <DeploymentCard key={d.id} d={d} />
          ))}
        </div>
      )}
    </CenterPanel>
  );
}
